import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackEventMp } from '@/components/Mixpanel';

interface FeatureSuggestionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userData: any;
  subscriptionStatus: any;
}

export const FeatureSuggestionModal = ({
  open,
  onOpenChange,
  userData,
  subscriptionStatus
}: FeatureSuggestionModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [suggestionType, setSuggestionType] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!suggestionType || description.length < 20) {
      toast({
        title: t('preferences.premium.suggestion.error'),
        description: t('preferences.premium.suggestion.minLength'),
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send notification to Slack
      const { error } = await supabase.functions.invoke('slack-notifications', {
        body: {
          table_name: 'feature_suggestion',
          record_data: {
            email: userData.email,
            subscription_id: userData.id,
            subscription_status: subscriptionStatus?.subscription_status || subscriptionStatus?.status || 'unknown',
            market_region: userData.market_region || 'unknown',
            suggestion_type: suggestionType,
            description: description.trim(),
            timestamp: new Date().toISOString()
          }
        }
      });

      if (error) throw error;

      setIsSuccess(true);
      
      trackEventMp('premium_suggestion_submitted', {
        suggestion_type: suggestionType,
        description_length: description.length,
        subscription_status: subscriptionStatus?.subscription_status || subscriptionStatus?.status || 'unknown'
      });

      toast({
        title: t('preferences.premium.suggestion.success'),
        description: t('preferences.premium.suggestion.successDescription')
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
        // Reset form
        setTimeout(() => {
          setSuggestionType('');
          setDescription('');
          setIsSuccess(false);
        }, 300);
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting suggestion:', error);
      toast({
        title: t('preferences.premium.suggestion.error'),
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('preferences.premium.suggestion.title')}</DialogTitle>
          <DialogDescription>
            {t('preferences.premium.suggestion.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 animate-bounce" />
            <p className="text-center text-lg font-semibold text-foreground">
              {t('preferences.premium.suggestion.success')}
            </p>
            <p className="text-center text-sm text-muted-foreground">
              {t('preferences.premium.suggestion.successDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Suggestion Type */}
            <div className="space-y-2">
              <Label htmlFor="suggestion-type">
                {t('preferences.premium.suggestion.typeLabel')}
              </Label>
              <Select value={suggestionType} onValueChange={setSuggestionType}>
                <SelectTrigger id="suggestion-type">
                  <SelectValue placeholder={t('preferences.premium.suggestion.typePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_module">
                    {t('preferences.premium.suggestion.types.newModule')}
                  </SelectItem>
                  <SelectItem value="integration">
                    {t('preferences.premium.suggestion.types.integration')}
                  </SelectItem>
                  <SelectItem value="improvement">
                    {t('preferences.premium.suggestion.types.improvement')}
                  </SelectItem>
                  <SelectItem value="other">
                    {t('preferences.premium.suggestion.types.other')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="suggestion-description">
                {t('preferences.premium.suggestion.descriptionLabel')}
              </Label>
              <Textarea
                id="suggestion-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('preferences.premium.suggestion.descriptionPlaceholder')}
                className="min-h-[120px] resize-none"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {description.length}/500
              </p>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !suggestionType || description.length < 20}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('preferences.premium.suggestion.submitting')}
                </>
              ) : (
                t('preferences.premium.suggestion.submit')
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
